import { fmtDateTime } from '../../lib/format';
import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Select,
  DatePicker,
  Tooltip,
  Empty,
  Drawer,
  Descriptions,
  Switch,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlayCircleOutlined,
  DownloadOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useAppSelector } from '@/app/hooks';
import { selectHasPermission } from '@/features/auth/authSlice';
import {
  getDryRuns,
  runDryRun,
  type DryRunResult,
} from '@/services/dataGovernanceService';
import {
  DryRunBanner,
  ComplianceStatusBadge,
  LegalHoldBadge,
} from '@/components/compliance';

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

const ACTIONS: DryRunResult['proposed_action'][] = [
  'REVIEW',
  'ANONYMIZE',
  'DELETE',
  'RETAIN',
];
const DECISIONS: DryRunResult['decision'][] = [
  'DUE',
  'NOT_DUE',
  'BLOCKED_BY_LEGAL_HOLD',
  'RETAIN_REQUIRED',
  'UNSUPPORTED',
];
const LEGAL_HOLDS: DryRunResult['legal_hold_status'][] = [
  'ACTIVE',
  'RELEASED',
  'NONE',
];

const fmtDate = (v?: string): string => (v ? dayjs(v).format('YYYY-MM-DD') : '—');

const buildCsv = (rows: DryRunResult[]): string => {
  const headers = [
    'run_id',
    'tenant_id',
    'subject_ref',
    'service_name',
    'record_type',
    'record_reference',
    'policy_matched',
    'retention_trigger_date',
    'due_date',
    'proposed_action',
    'legal_hold_status',
    'decision',
    'evaluated_at',
  ];
  const escape = (val: unknown): string => {
    const s = val === undefined || val === null ? '' : String(val);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map((r) =>
    [
      r.run_id,
      r.tenant_id,
      r.subject_ref,
      r.service_name,
      r.record_type,
      r.record_reference,
      r.policy_matched,
      r.retention_trigger_date,
      r.due_date,
      r.proposed_action,
      r.legal_hold_status,
      r.decision,
      r.evaluated_at,
    ]
      .map(escape)
      .join(','),
  );
  return [headers.join(','), ...lines].join('\n');
};

const downloadCsv = (rows: DryRunResult[]): void => {
  const csv = buildCsv(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `retention-dry-run-${dayjs().format('YYYYMMDD-HHmmss')}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

function RetentionDryRunPage() {
  const canView = useAppSelector(selectHasPermission('compliance:dry_run:view'));
  const canRun = useAppSelector(selectHasPermission('compliance:dry_run:run'));

  const [loading, setLoading] = useState<boolean>(false);
  const [running, setRunning] = useState<boolean>(false);
  const [rows, setRows] = useState<DryRunResult[]>([]);

  // filters
  const [service, setService] = useState<string | undefined>();
  const [recordType, setRecordType] = useState<string | undefined>();
  const [action, setAction] = useState<DryRunResult['proposed_action'] | undefined>();
  const [decision, setDecision] = useState<DryRunResult['decision'] | undefined>();
  const [legalHold, setLegalHold] = useState<DryRunResult['legal_hold_status'] | undefined>();
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [dueOnly, setDueOnly] = useState<boolean>(false);

  // drawer
  const [selected, setSelected] = useState<DryRunResult | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await getDryRuns();
      setRows(data);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load dry-run results.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) {
      load();
    }
  }, [canView]);

  const serviceOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.service_name))).sort(),
    [rows],
  );
  const recordTypeOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.record_type))).sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (service && r.service_name !== service) return false;
      if (recordType && r.record_type !== recordType) return false;
      if (action && r.proposed_action !== action) return false;
      if (decision && r.decision !== decision) return false;
      if (legalHold && r.legal_hold_status !== legalHold) return false;
      if (dueOnly && r.decision !== 'DUE') return false;
      if (range && (range[0] || range[1]) && r.due_date) {
        const due = dayjs(r.due_date);
        if (range[0] && due.isBefore(range[0], 'day')) return false;
        if (range[1] && due.isAfter(range[1], 'day')) return false;
      } else if (range && (range[0] || range[1]) && !r.due_date) {
        return false;
      }
      return true;
    });
  }, [rows, service, recordType, action, decision, legalHold, dueOnly, range]);

  const handleRun = async (): Promise<void> => {
    setRunning(true);
    try {
      const result = await runDryRun();
      message.info(result.note);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load dry-run results.');
    } finally {
      setRunning(false);
    }
  };

  const columns: ColumnsType<DryRunResult> = [
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      render: (v: string) => <Text code>{v?.slice(0, 8) || '—'}</Text>,
    },
    { title: 'Subject', dataIndex: 'subject_ref', key: 'subject_ref' },
    {
      title: 'Service',
      dataIndex: 'service_name',
      key: 'service_name',
      render: (v: string) => <Tag>{v}</Tag>,
    },
    { title: 'Record type', dataIndex: 'record_type', key: 'record_type' },
    { title: 'Record ref', dataIndex: 'record_reference', key: 'record_reference' },
    {
      title: 'Policy matched',
      dataIndex: 'policy_matched',
      key: 'policy_matched',
      render: (v?: string) => v || '—',
    },
    {
      title: 'Trigger date',
      dataIndex: 'retention_trigger_date',
      key: 'retention_trigger_date',
      render: (v?: string) => fmtDate(v),
    },
    {
      title: 'Due date',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (v?: string) => fmtDate(v),
    },
    {
      title: 'Proposed action',
      dataIndex: 'proposed_action',
      key: 'proposed_action',
      render: (v: DryRunResult['proposed_action']) =>
        v === 'DELETE' ? (
          <Tooltip title="Destructive — proposed only, never executed" color="red">
            <span>
              <ComplianceStatusBadge kind="retention_action" value={v} />
            </span>
          </Tooltip>
        ) : (
          <ComplianceStatusBadge kind="retention_action" value={v} />
        ),
    },
    {
      title: 'Legal hold',
      dataIndex: 'legal_hold_status',
      key: 'legal_hold_status',
      render: (v: DryRunResult['legal_hold_status']) => (
        <LegalHoldBadge active={v === 'ACTIVE'} />
      ),
    },
    {
      title: 'Decision',
      dataIndex: 'decision',
      key: 'decision',
      render: (v: DryRunResult['decision']) => (
        <ComplianceStatusBadge kind="dry_run_decision" value={v} />
      ),
    },
    {
      title: 'Evaluated at',
      dataIndex: 'evaluated_at',
      key: 'evaluated_at',
      render: (v: string) => (v ? fmtDateTime(v) : '—'),
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      render: (_: unknown, record: DryRunResult) => (
        <Button type="link" size="small" onClick={() => setSelected(record)}>
          Details
        </Button>
      ),
    },
  ];

  if (!canView) {
    return (
      <Card>
        <Empty description="You do not have permission to view retention dry-run results." />
      </Card>
    );
  }

  const supportsExecution = selected?.service_name === 'compliance-service';

  return (
    <div>
      <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 16 }}>
        <Title level={3} style={{ marginBottom: 0 }}>
          Retention Dry-Run Review
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          What the retention worker would do — without modifying data.
        </Paragraph>
      </Space>

      <DryRunBanner message="DRY-RUN ONLY — no records were modified." />

      <Card style={{ marginTop: 16 }}>
        <Space wrap style={{ marginBottom: 16 }}>
          <Tooltip title={canRun ? undefined : 'You lack permission to run a dry-run.'}>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={running}
              disabled={!canRun}
              onClick={handleRun}
            >
              Run Dry-Run
            </Button>
          </Tooltip>
          <Button
            icon={<DownloadOutlined />}
            disabled={filtered.length === 0}
            onClick={() => downloadCsv(filtered)}
          >
            Export CSV
          </Button>
          <Tooltip title="Report export not available in this release">
            <Button icon={<FileTextOutlined />} disabled>
              Download Dry-Run Report
            </Button>
          </Tooltip>
        </Space>

        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            allowClear
            placeholder="Service"
            style={{ width: 180 }}
            value={service}
            onChange={setService}
            options={serviceOptions.map((s) => ({ label: s, value: s }))}
          />
          <Select
            allowClear
            placeholder="Record category"
            style={{ width: 180 }}
            value={recordType}
            onChange={setRecordType}
            options={recordTypeOptions.map((s) => ({ label: s, value: s }))}
          />
          <Select
            allowClear
            placeholder="Proposed action"
            style={{ width: 170 }}
            value={action}
            onChange={setAction}
            options={ACTIONS.map((a) => ({ label: a, value: a }))}
          />
          <Select
            allowClear
            placeholder="Decision"
            style={{ width: 200 }}
            value={decision}
            onChange={setDecision}
            options={DECISIONS.map((d) => ({ label: d, value: d }))}
          />
          <Select
            allowClear
            placeholder="Legal hold"
            style={{ width: 150 }}
            value={legalHold}
            onChange={setLegalHold}
            options={LEGAL_HOLDS.map((l) => ({ label: l, value: l }))}
          />
          <RangePicker
            value={range as [Dayjs, Dayjs] | null}
            onChange={(vals) => setRange(vals as [Dayjs | null, Dayjs | null] | null)}
          />
          <Space size={6}>
            <Switch checked={dueOnly} onChange={setDueOnly} />
            <Text>Due only</Text>
          </Space>
        </Space>

        <Table<DryRunResult>
          rowKey="id"
          columns={columns}
          dataSource={filtered}
          loading={loading}
          scroll={{ x: 1600 }}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span>
                    No dry-run results yet. The retention worker runs server-side in
                    dry-run mode; per-record result rows will appear here once the backend
                    persists them.
                  </span>
                }
              />
            ),
          }}
        />
      </Card>

      <Drawer
        title="Dry-Run Record Details"
        width={560}
        open={!!selected}
        onClose={() => setSelected(null)}
      >
        {selected && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <DryRunBanner message="DRY-RUN ONLY — no records were modified." />
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Why this record matched">
                Subject <Text code>{selected.subject_ref}</Text> in{' '}
                <Tag>{selected.service_name}</Tag> record{' '}
                <Text code>{selected.record_reference}</Text> ({selected.record_type}) was
                evaluated against retention policy.
              </Descriptions.Item>
              <Descriptions.Item label="Policy matched">
                {selected.policy_matched || '— (no explicit policy matched)'}
              </Descriptions.Item>
              <Descriptions.Item label="Why the action is proposed">
                <Space>
                  <ComplianceStatusBadge
                    kind="dry_run_decision"
                    value={selected.decision}
                  />
                  <span>proposes</span>
                  <ComplianceStatusBadge
                    kind="retention_action"
                    value={selected.proposed_action}
                  />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Trigger / due">
                Trigger {fmtDate(selected.retention_trigger_date || undefined)} · Due{' '}
                {fmtDate(selected.due_date || undefined)}
              </Descriptions.Item>
              <Descriptions.Item label="Did legal hold block it?">
                <Space>
                  <LegalHoldBadge active={selected.legal_hold_status === 'ACTIVE'} />
                  {selected.decision === 'BLOCKED_BY_LEGAL_HOLD' ? (
                    <Text type="danger">
                      Yes — an active legal hold blocks any action on this record.
                    </Text>
                  ) : (
                    <Text type="secondary">
                      No — legal hold did not block this evaluation.
                    </Text>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="What would happen in future destructive mode">
                {selected.proposed_action === 'DELETE' ? (
                  <Text type="danger">
                    The record would be permanently deleted. Proposed only — never
                    executed in this release.
                  </Text>
                ) : selected.proposed_action === 'ANONYMIZE' ? (
                  <Text>
                    Personal identifiers would be anonymized. Proposed only in this
                    release.
                  </Text>
                ) : selected.proposed_action === 'RETAIN' ? (
                  <Text>The record would be retained; no destructive action.</Text>
                ) : (
                  <Text>The record would be flagged for manual review.</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Does the service support execution?">
                {supportsExecution ? (
                  <Text>
                    Yes — compliance-service supports execution (still preview-only here).
                  </Text>
                ) : (
                  <Text type="secondary">
                    <InfoCircleOutlined /> Preview only in this release.
                  </Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Evaluated at">
                {selected.evaluated_at
                  ? fmtDateTime(selected.evaluated_at)
                  : '—'}
              </Descriptions.Item>
            </Descriptions>
          </Space>
        )}
      </Drawer>
    </div>
  );
}

export default RetentionDryRunPage;
